import clsx from 'clsx';
import { Dispatch, SetStateAction, useState } from 'react';
import { Icons } from '../../components';
import { KNOWLEDGE_HUB_TYPE_CONFIG, TABLE_ROW_HOVER_CLASSES } from '../../constants';
import { KnowledgeHubRecord } from '../../services';
import { KnowledgeHubRecordType } from '../../types';
import { getKnowledgeHubIcon } from '../../utils';
import CreateKnowledgeHubItem from './CreateKnowledgeHubItem';
import EditKnowledgeHubItem from './EditKnowledgeHubItem';
import KnowledgeHubItemActions from './KnowledgeHubItemActions';

export const KNOWLEDGE_HUB_NEW_RECORD_PREFIX = 'new-';
export const KNOWLEDGE_HUB_ITEM_MIN_HEIGHT = 48;

interface KnowledgeHubItemProps {
  currPage: number;
  currPageSize: number;
  initialValue: string;
  recordId: string;
  totalPages: number;
  type: KnowledgeHubRecordType;
  refetchKnowledgeHub: () => void;
  setCurrPage: Dispatch<SetStateAction<number>>;
  setDraftRecords: Dispatch<SetStateAction<Pick<KnowledgeHubRecord, 'id' | 'content'>[]>>;
  setExistingRecords: Dispatch<SetStateAction<KnowledgeHubRecord[]>>;
}

const KnowledgeHubItem = ({
  currPage,
  currPageSize,
  initialValue,
  recordId,
  totalPages,
  type,
  refetchKnowledgeHub,
  setCurrPage,
  setDraftRecords,
  setExistingRecords,
}: KnowledgeHubItemProps) => {
  const isNewRecord = recordId.startsWith(KNOWLEDGE_HUB_NEW_RECORD_PREFIX);

  const [isInEditMode, setIsInEditMode] = useState(isNewRecord);
  const [isHovering, setIsHovering] = useState(false);
  const [value, setValue] = useState(initialValue);

  const icon = getKnowledgeHubIcon(type);
  const iconColor = KNOWLEDGE_HUB_TYPE_CONFIG[type].color;

  return (
    <div
      className={clsx('flex items-center justify-between gap-2 p-3', !isInEditMode && TABLE_ROW_HOVER_CLASSES)}
      onMouseEnter={!isInEditMode ? () => setIsHovering(true) : undefined}
      onMouseLeave={!isInEditMode ? () => setIsHovering(false) : undefined}
    >
      <div className="flex w-full items-center gap-6">
        <Icons icon={icon} color={iconColor} />
        {!isInEditMode && (
          <div className="flex items-center" style={{ minHeight: KNOWLEDGE_HUB_ITEM_MIN_HEIGHT }}>
            {value}
          </div>
        )}
        {isInEditMode && isNewRecord && (
          <CreateKnowledgeHubItem
            currPage={currPage}
            currPageSize={currPageSize}
            recordId={recordId}
            type={type}
            value={value}
            setCurrPage={setCurrPage}
            setDraftRecords={setDraftRecords}
            setExistingRecords={setExistingRecords}
            setValue={setValue}
          />
        )}
        {isInEditMode && !isNewRecord && (
          <EditKnowledgeHubItem
            currPage={currPage}
            initialValue={initialValue}
            recordId={recordId}
            type={type}
            value={value}
            setCurrPage={setCurrPage}
            setExistingRecords={setExistingRecords}
            exitEditMode={() => {
              setIsInEditMode(false);
              setIsHovering(false);
            }}
            setValue={setValue}
          />
        )}
      </div>
      {isHovering && !isInEditMode && (
        <KnowledgeHubItemActions
          currPage={currPage}
          currPageSize={currPageSize}
          recordId={recordId}
          enterEditMode={() => {
            setIsInEditMode(true);
            setIsHovering(false);
          }}
          totalPages={totalPages}
          refetchKnowledgeHub={refetchKnowledgeHub}
          setCurrPage={setCurrPage}
        />
      )}
    </div>
  );
};

export default KnowledgeHubItem;
